import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import {
  MailOutline,
  LockOutlined,
  Visibility,
  VisibilityOff,
  PersonOutline,
  PhoneOutlined,
  ArrowForward,
  Check,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  validatePasswordStrength,
  getPasswordStrength,
} from '../utils/passwordValidation';
import AuthLayout from './auth/AuthLayout';
import {
  authColors,
  fontStack,
  AuthField,
  AuthButton,
} from './auth/authStyles';

const FieldErrorBubble = ({ message }) => {
  if (!message) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        alignSelf: 'flex-start',
        marginTop: '-2px',
        padding: '8px 11px',
        borderRadius: '9px',
        backgroundColor: '#FFF1F2',
        border: '1px solid rgba(248, 113, 113, 0.55)',
        color: '#991B1B',
        fontSize: '12px',
        fontWeight: 600,
        fontFamily: fontStack,
        boxShadow: '0 10px 24px rgba(0, 0, 0, 0.22)',

        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-6px',
          left: '18px',
          width: 10,
          height: 10,
          backgroundColor: '#FFF1F2',
          borderLeft: '1px solid rgba(248, 113, 113, 0.55)',
          borderTop: '1px solid rgba(248, 113, 113, 0.55)',
          transform: 'rotate(45deg)',
        },
      }}
    >
      {message}
    </Box>
  );
};

const Register = ({ onSwitchToLogin, onBackToWelcome }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptCgu, setAcceptCgu] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, errors: [] });
  const { login } = useAuth();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    setFormErrors((previousErrors) => ({
      ...previousErrors,
      [name]: '',
      form: '',
    }));
  };

  useEffect(() => {
    if (formData.password) {
      setPasswordValidation(validatePasswordStrength(formData.password));
      setPasswordStrength(getPasswordStrength(formData.password));
    } else {
      setPasswordValidation({ isValid: false, errors: [] });
      setPasswordStrength(0);
    }
  }, [formData.password]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = {};

    if (!passwordValidation.isValid) {
      nextErrors.password =
        'Le mot de passe ne respecte pas les critères de sécurité.';
    }

    if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword =
        'Les mots de passe ne correspondent pas.';
    }

    if (!acceptCgu) {
      nextErrors.acceptCgu =
        'Veuillez accepter les CGU et la politique de confidentialité.';
    }

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    setFormErrors({});
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phoneNumber: formData.phoneNumber || null,
        },
        { withCredentials: true }
      );

      login(response.data.user, response.data.token);
    } catch (err) {
      const apiError =
        err.response?.data?.error ||
        "Erreur lors de l'inscription.";

      if (apiError.toLowerCase().includes('email')) {
        setFormErrors({
          email: apiError,
        });
      } else {
        setFormErrors({
          form: apiError,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // 4 barres de force du mot de passe (cf. maquette)
  const strengthBars = [25, 50, 75, 100];
  const barColor = (threshold) => {
    if (passwordStrength < threshold) return authColors.borderSubtle;
    if (passwordStrength < 40) return authColors.danger;
    if (passwordStrength < 70) return authColors.warning;
    return authColors.accentGreen;
  };

  return (
    <AuthLayout
      brandTitle="Rejoignez la supervision nouvelle génération."
      brandSubtitle="Créez votre compte pour suivre vos boitiers Climatrack, Aquacheck et Greenwatch depuis une plateforme unique."
      maxWidth={480}
      onBack={onBackToWelcome}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* En-tête */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography sx={{ color: authColors.textPrimary, fontSize: { xs: '23px', md: '28px' }, fontWeight: 700, fontFamily: fontStack }}>
            Créez votre compte
          </Typography>
          <Typography sx={{ color: authColors.textMuted, fontSize: '13px', fontFamily: fontStack }}>
            Il suffit de 30 secondes. Vos boitiers seront liés à votre profil.
          </Typography>
        </Box>

        {/* Prénom + Nom (empilés sur mobile) */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '12px' }}>
          <AuthField
            label="Prénom"
            icon={<PersonOutline />}
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
          <AuthField
            label="Nom"
            icon={<PersonOutline />}
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </Box>

        <AuthField
          label="Adresse e-mail"
          icon={<MailOutline />}
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
        />
        <FieldErrorBubble message={formErrors.email} />

        <AuthField
          label="Numéro de téléphone (optionnel)"
          icon={<PhoneOutlined />}
          name="phoneNumber"
          type="tel"
          value={formData.phoneNumber}
          onChange={handleChange}
          placeholder="06 12 34 56 78"
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <AuthField
            label="Mot de passe"
            icon={<LockOutlined />}
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            placeholder="••••••••••••"
            required
            autoComplete="new-password"
            endIcon={showPassword ? <VisibilityOff /> : <Visibility />}
            onEndIconClick={() => setShowPassword(!showPassword)}
          />
          <FieldErrorBubble message={formErrors.password} />
          {/* Barres de force */}
          {formData.password && (
            <Box sx={{ display: 'flex', gap: '6px', pt: '6px' }}>
              {strengthBars.map((threshold) => (
                <Box
                  key={threshold}
                  sx={{
                    flex: 1,
                    height: 4,
                    borderRadius: '4px',
                    backgroundColor: barColor(threshold),
                    transition: 'background-color 0.2s ease',
                  }}
                />
              ))}
            </Box>
          )}
          <Typography sx={{ color: authColors.textPlaceholder, fontSize: '11px', fontFamily: fontStack }}>
            Doit contenir 8+ caractères, 1 majuscule, 1 chiffre et 1 caractère spécial.
          </Typography>
        </Box>

        <AuthField
          label="Confirmer le mot de passe"
          icon={<LockOutlined />}
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="••••••••••••"
          required
          autoComplete="new-password"
          endIcon={showConfirmPassword ? <VisibilityOff /> : <Visibility />}
          onEndIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
        />
        <FieldErrorBubble message={formErrors.confirmPassword} />

        {/* CGU */}
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}
          onClick={() => {
            setAcceptCgu(!acceptCgu);

            setFormErrors((previousErrors) => ({
              ...previousErrors,
              acceptCgu: '',
              form: '',
            }));
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              flexShrink: 0,
              mt: '2px',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: acceptCgu ? authColors.accentGreen : 'transparent',
              border: `1px solid ${acceptCgu ? authColors.accentGreen : authColors.borderSubtle}`,
            }}
          >
            {acceptCgu && <Check sx={{ fontSize: 12, color: authColors.onAccent }} />}
          </Box>
          <Typography sx={{ color: authColors.textMuted, fontSize: '12px', lineHeight: 1.45, fontFamily: fontStack }}>
            J'accepte les CGU et la politique de confidentialité d'Ecowatch 360.
          </Typography>
        </Box>
        <FieldErrorBubble message={formErrors.acceptCgu} />
        <FieldErrorBubble message={formErrors.form} />

        <AuthButton loading={loading} sx={{ height: '50px' }}>
          {loading ? 'Création...' : 'Créer mon compte'}
          {!loading && <ArrowForward sx={{ fontSize: 16 }} />}
        </AuthButton>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
          <Typography sx={{ color: authColors.textMuted, fontSize: '13px', fontFamily: fontStack }}>
            Déjà un compte ?
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onSwitchToLogin}
            sx={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0,
              color: authColors.accentGreen,
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: fontStack,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Se connecter
          </Box>
        </Box>
      </Box>
    </AuthLayout>
  );
};

export default Register;
